import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  message,
  Space,
  Typography,
  Tag,
  Popconfirm,
  Upload,
  Card,
  Statistic,
  Row,
  Col
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, CheckCircleOutlined, ClockCircleOutlined, GiftOutlined } from '@ant-design/icons';
import axios from '../../utils/request';
import dayjs from 'dayjs';

const { Title } = Typography;

interface Coupon {
  id: number;
  _id?: string;
  code: string;
  name: string;
  expiryDate: string;
  isUsed: boolean;
  usedAt?: string;
  usedBy?: { username: string };
  ekokNumber?: string;
  customerName?: string;
  phone?: string;
  email?: string;
  createdAt: string;
}

const CouponManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();
  const [parsedCoupons, setParsedCoupons] = useState<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [statistics, setStatistics] = useState({
    totalCoupons: 0,
    usedCoupons: 0
  });

  const fetchCoupons = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`/api/coupons/list?page=${page}&limit=${pageSize}${searchParam}`);
      setCoupons(response.data.coupons);
      setPagination({
        current: page,
        pageSize,
        total: response.data.pagination.total
      });
    } catch (error: any) {
      message.error(error.response?.data?.message || '获取优惠券列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get('/api/coupons/statistics');
      setStatistics({
        totalCoupons: response.data.totalCoupons,
        usedCoupons: response.data.usedCoupons
      });
    } catch (error: any) {
      console.error('获取统计数据失败:', error);
    }
  };

  useEffect(() => {
    fetchCoupons();
    fetchStatistics();
  }, []);

  const handleCreate = async (values: any) => {
    try {
      await axios.post('/api/coupons/create', {
        ...values,
        expiryDate: values.expiryDate.toISOString()
      });
      message.success('优惠券创建成功');
      setModalVisible(false);
      form.resetFields();
      fetchCoupons(pagination.current, pagination.pageSize, searchText);
      fetchStatistics();
    } catch (error: any) {
      message.error(error.response?.data?.message || '创建优惠券失败');
    }
  };

  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      ...coupon,
      expiryDate: dayjs(coupon.expiryDate)
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`/api/coupons/${id}`);
      message.success('优惠券删除成功');
      fetchCoupons(pagination.current, pagination.pageSize, searchText);
      fetchStatistics();
    } catch (error: any) {
      message.error(error.response?.data?.message || '删除优惠券失败');
    }
  };

  const handleImport = async (values: any) => {
    try {
      const { coupons } = values;
      const couponList = coupons.split('\n').filter((line: string) => line.trim()).map((line: string) => {
        const [code, name, expiryDate] = line.split(',').map(s => s.trim());
        return {
          code,
          name,
          expiryDate
        };
      });

      await axios.post('/api/coupons/batch-create', { coupons: couponList });
      message.success(`成功导入 ${couponList.length} 个优惠券`);
      setImportModalVisible(false);
      importForm.resetFields();
      fetchCoupons(pagination.current, pagination.pageSize, searchText);
      fetchStatistics();
    } catch (error: any) {
      message.error(error.response?.data?.message || '导入失败');
    }
  };

  const downloadTemplate = () => {
    const csvContent = `优惠码,优惠券名称,有效期
SAVE20,满减20元券,2024-12-31
SAVE50,满减50元券,2024-12-31
SAVE100,满减100元券,2024-12-31
WELCOME10,新用户券,2024-12-31
VIP200,VIP专享券,2024-12-31`;

    const blob = new Blob(['\ufeff' + csvContent], { 
      type: 'text/csv;charset=utf-8;' 
    });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', '优惠券导入模板.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('模板下载成功');
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        console.log('Raw CSV content preview:', csv.substring(0, 200));
        
        // 处理不同的换行符
        const normalizedCsv = csv.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = normalizedCsv.split('\n').filter(line => line.trim());
        
        console.log('Total lines found:', lines.length);
        console.log('First few lines:', lines.slice(0, 3));
        
        if (lines.length < 2) {
          message.error('CSV文件格式错误，至少需要包含表头和一行数据');
          return;
        }

        // 跳过表头，解析数据
        const parsedData = [];
        const errors = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // 跳过空行
          
          try {
            // 更智能的CSV解析，处理可能包含逗号的字段
            const fields = [];
            let current = '';
            let inQuotes = false;
            
            for (let j = 0; j < line.length; j++) {
              const char = line[j];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                fields.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            fields.push(current.trim()); // 添加最后一个字段
            
            // 清理字段中的引号
            const [code, name, expiryDate] = fields.map(f => f.replace(/^"|"$/g, '').trim());
            
            console.log(`Row ${i + 1}:`, { code, name, expiryDate });
            
            if (!code || !name || !expiryDate) {
              errors.push(`第${i + 1}行数据格式错误：缺少必填字段 (${code}, ${name}, ${expiryDate})`);
              continue;
            }

            // 验证日期格式 - 更宽松的日期验证
            const dateRegex = /^\d{4}-\d{1,2}-\d{1,2}$/;
            if (!dateRegex.test(expiryDate)) {
              errors.push(`第${i + 1}行日期格式错误：${expiryDate}，请使用YYYY-MM-DD格式`);
              continue;
            }

            parsedData.push({
              key: i - 1,
              code,
              name,
              expiryDate,
              status: '待导入'
            });
          } catch (lineError: any) {
            errors.push(`第${i + 1}行解析错误：${lineError.message}`);
          }
        }

        console.log('Parsed data:', parsedData);
        console.log('Errors:', errors);

        if (parsedData.length === 0) {
          message.error('没有解析到有效的优惠券数据');
          if (errors.length > 0) {
            console.error('解析错误:', errors);
            message.error(`解析错误: ${errors[0]}`);
          }
          setParsedCoupons([]);
          return;
        }

        setParsedCoupons(parsedData);
        let successMsg = `成功解析 ${parsedData.length} 个优惠券`;
        if (errors.length > 0) {
          successMsg += `，${errors.length} 行有错误`;
        }
        message.success(successMsg);
        
        if (errors.length > 0) {
          console.warn('部分行解析失败:', errors);
        }
      } catch (error: any) {
        console.error('文件解析失败:', error);
        message.error(`文件解析失败: ${error.message}`);
        setParsedCoupons([]);
      }
    };
    
    reader.readAsText(file, 'UTF-8');
    return false; // 阻止默认上传行为
  };

  const handleImportParsedCoupons = async () => {
    if (parsedCoupons.length === 0) {
      message.warning('请先上传CSV文件');
      return;
    }

    // 显示加载提示
    const hideLoading = message.loading(
      `正在导入 ${parsedCoupons.length} 个优惠券，请稍候...`, 
      0 // 0表示不自动关闭
    );

    try {
      const couponList = parsedCoupons.map(item => ({
        code: item.code,
        name: item.name,
        expiryDate: item.expiryDate
      }));

      console.log(`开始导入 ${couponList.length} 个优惠券`);
      const response = await axios.post('/api/coupons/batch-create', { coupons: couponList }, {
        timeout: 300000 // 5分钟超时
      });
      
      hideLoading();
      message.success(`成功导入 ${response.data.success} 个优惠券`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        message.warning(`${response.data.failed} 个优惠券导入失败，详情请查看控制台`);
        console.log('导入错误详情:', response.data.errors);
      }

      setImportModalVisible(false);
      setParsedCoupons([]);
      setFileList([]);
      importForm.resetFields();
      fetchCoupons(pagination.current, pagination.pageSize, searchText);
      fetchStatistics();
    } catch (error: any) {
      hideLoading();
      console.error('导入失败:', error);
      if (error.code === 'ECONNABORTED') {
        message.error('导入超时，请检查网络连接或减少数据量');
      } else if (error.response?.status === 413) {
        message.error('数据量过大，请分批导入');
      } else {
        message.error(error.response?.data?.message || '批量导入失败');
      }
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCoupons(1, pagination.pageSize, value);
  };

  const columns = [
    {
      title: '优惠码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: '优惠券名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '有效期',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '状态',
      dataIndex: 'isUsed',
      key: 'isUsed',
      width: 80,
      render: (isUsed: boolean, record: Coupon) => {
        if (isUsed) {
          return <Tag color="red">已使用</Tag>;
        }
        if (dayjs() > dayjs(record.expiryDate)) {
          return <Tag color="orange">已过期</Tag>;
        }
        return <Tag color="green">可用</Tag>;
      },
    },
    {
      title: '使用时间',
      dataIndex: 'usedAt',
      key: 'usedAt',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'EKOK号码',
      dataIndex: 'ekokNumber',
      key: 'ekokNumber',
      width: 120,
      render: (ekokNumber: string) => ekokNumber || '-',
    },
    {
      title: '客户姓名',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 100,
      render: (customerName: string) => customerName || '-',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone: string) => phone || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      render: (email: string) => email || '-',
    },
    {
      title: '使用者',
      dataIndex: 'usedBy',
      key: 'usedBy',
      width: 100,
      render: (user: any) => user?.username || '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: Coupon) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.isUsed}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个优惠券吗？"
            onConfirm={() => handleDelete(String(record.id || record._id))}
            disabled={record.isUsed}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={record.isUsed}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="优惠券总数"
              value={statistics.totalCoupons}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="已核销数量"
              value={statistics.usedCoupons}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="未核销数量"
              value={statistics.totalCoupons - statistics.usedCoupons}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>优惠券管理</Title>
        <Space size="middle">
          <Input.Search
            placeholder="搜索优惠码、名称、手机号或客户姓名"
            allowClear
            style={{ width: 300 }}
            onSearch={handleSearch}
            onChange={(e) => {
              if (!e.target.value) {
                handleSearch('');
              }
            }}
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
          >
            下载模板
          </Button>
          <Button
            icon={<UploadOutlined />}
            onClick={() => {
              importForm.resetFields();
              setImportModalVisible(true);
            }}
          >
            批量导入
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingCoupon(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            新增优惠券
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={coupons}
        rowKey={(record) => String(record.id || record._id)}
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            fetchCoupons(page, pageSize, searchText);
          }
        }}
      />

      <Modal
        title={editingCoupon ? '编辑优惠券' : '新增优惠券'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="code"
            label="优惠码"
            rules={[{ required: true, message: '请输入优惠码' }]}
          >
            <Input placeholder="请输入优惠码" />
          </Form.Item>

          <Form.Item
            name="name"
            label="优惠券名称"
            rules={[{ required: true, message: '请输入优惠券名称' }]}
          >
            <Input placeholder="请输入优惠券名称" />
          </Form.Item>

          <Form.Item
            name="expiryDate"
            label="有效期"
            rules={[{ required: true, message: '请选择有效期' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="请选择有效期"
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCoupon ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量导入优惠券"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <Typography.Text type="secondary">
              请按照以下格式输入优惠券数据，每行一个优惠券：<br />
              <code>优惠码,优惠券名称,有效期(YYYY-MM-DD)</code><br />
              示例：SAVE20,满减20元券,2024-12-31
            </Typography.Text>
            <Button 
              type="link" 
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
              style={{ padding: 0 }}
            >
              下载模板
            </Button>
          </div>
        </div>
        
        <div style={{ marginBottom: '24px' }}>
          <Typography.Text strong>方式一：上传CSV文件</Typography.Text>
          <div style={{ marginTop: '8px' }}>
            <Upload
              beforeUpload={handleFileUpload}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              accept=".csv"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>选择CSV文件</Button>
            </Upload>
          </div>
        </div>

        {parsedCoupons.length > 0 && (
          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
            <Typography.Text strong style={{ color: '#52c41a' }}>✅ CSV文件解析成功！共 {parsedCoupons.length} 个优惠券待导入</Typography.Text>
            <Table
              dataSource={parsedCoupons}
              columns={[
                { title: '优惠码', dataIndex: 'code', key: 'code' },
                { title: '名称', dataIndex: 'name', key: 'name' },
                { title: '有效期', dataIndex: 'expiryDate', key: 'expiryDate' },
                { title: '状态', dataIndex: 'status', key: 'status', render: () => <Tag color="blue">待导入</Tag> }
              ]}
              pagination={false}
              size="small"
              style={{ maxHeight: '300px', overflow: 'auto', marginTop: '8px' }}
            />
            <div style={{ marginTop: '16px', textAlign: 'center', padding: '8px', backgroundColor: '#fff', borderRadius: '4px' }}>
              <Space size="large">
                <Button onClick={() => {
                  setParsedCoupons([]);
                  setFileList([]);
                }}>
                  清空重选
                </Button>
                <Button type="primary" size="large" onClick={handleImportParsedCoupons}>
                  🚀 立即导入 ({parsedCoupons.length} 个优惠券)
                </Button>
              </Space>
            </div>
          </div>
        )}

        <Typography.Text strong>方式二：手动输入数据</Typography.Text>
        <Form
          form={importForm}
          layout="vertical"
          onFinish={handleImport}
          style={{ marginTop: '8px' }}
        >
          <Form.Item
            name="coupons"
            label="优惠券数据"
            rules={[{ required: parsedCoupons.length === 0, message: '请上传CSV文件或输入优惠券数据' }]}
          >
            <Input.TextArea
              placeholder="SAVE20,满减20元券,2024-12-31&#10;SAVE50,满减50元券,2024-12-31&#10;SAVE100,满减100元券,2024-12-31"
              rows={6}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setImportModalVisible(false);
                setParsedCoupons([]);
                setFileList([]);
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" disabled={parsedCoupons.length > 0}>
                手动导入
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CouponManagement;